import React, { useState } from 'react'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import LeadCard from './LeadCard'
import { stageMeta } from '../kit/helpers'

const DraggableLead = ({ row, onOpen, onMenu }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: row.id,
    data: { row },
  })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{ opacity: isDragging ? 0.35 : 1, touchAction: 'none' }}
    >
      <LeadCard row={row} onOpen={() => onOpen(row)} onMenu={(event) => onMenu(event, row)} />
    </Box>
  )
}

const BoardColumn = ({ stage, rows, onOpen, onMenu }) => {
  const meta = stageMeta(stage)
  const { setNodeRef, isOver } = useDroppable({ id: stage })
  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        width: 300,
        minWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: isOver ? meta.color : '#E2E8F0',
        borderRadius: 3,
        bgcolor: isOver ? alpha(meta.color, 0.08) : '#F8FAFC',
        overflow: 'hidden',
        transition: 'background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}
      >
        <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
          <Box minWidth={0}>
            <Typography fontWeight={700} noWrap lineHeight={1.2}>
              {meta.label}
            </Typography>
            {meta.hint && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {meta.hint}
              </Typography>
            )}
          </Box>
        </Stack>
        <Chip
          label={rows.length}
          size="small"
          sx={{ minWidth: 34, fontWeight: 700, bgcolor: alpha(meta.color, 0.14), color: meta.color }}
        />
      </Stack>
      <Stack padding={1} gap={1} flex={1} minHeight={180} sx={{ overflowY: 'auto' }}>
        {rows.length === 0 && (
          <Paper
            variant="outlined"
            sx={{ p: 2, textAlign: 'center', bgcolor: '#FFFFFF', borderStyle: 'dashed', borderRadius: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {isOver ? 'Drop to move here' : 'Nothing here'}
            </Typography>
          </Paper>
        )}
        {rows.map((row) => (
          <DraggableLead key={row.id} row={row} onOpen={onOpen} onMenu={onMenu} />
        ))}
      </Stack>
    </Paper>
  )
}

// Kanban of pipeline rows grouped by stage. Dropping a card on another
// column calls onMoveStage(row, stage); the parent owns the data.
const PipelineBoard = ({ stages, rows, onOpen, onMenu, onMoveStage }) => {
  const [activeRow, setActiveRow] = useState(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => setActiveRow(active.data.current?.row ?? null)}
      onDragCancel={() => setActiveRow(null)}
      onDragEnd={({ active, over }) => {
        setActiveRow(null)
        const row = active.data.current?.row
        if (!row || !over) return
        const stage = String(over.id)
        if (stage === row.stage) return
        onMoveStage(row, stage)
      }}
    >
      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Stack direction="row" gap={1.5} alignItems="stretch" sx={{ minHeight: 420 }}>
          {stages.map((stage) => (
            <BoardColumn
              key={stage}
              stage={stage}
              rows={rows.filter((row) => row.stage === stage)}
              onOpen={onOpen}
              onMenu={onMenu}
            />
          ))}
        </Stack>
      </Box>
      <DragOverlay dropAnimation={null}>
        {activeRow ? (
          <Box width={284}>
            <LeadCard row={activeRow} dragging />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default PipelineBoard
